import { HStack, VStack, useTheme, IconButton, Box, useBreakpointValue, IBoxProps } from 'native-base';
import { useTranslation } from 'react-i18next';

import { ArrowSquareLeftIcon, ArrowSquareRightIcon, MinusCircleIcon, PlusCircleIcon, Text } from '@pimm/base';
import { WidgetModule } from '../context/widget-modules.context';

type WidgetSlotProps = IBoxProps & {
  module?: WidgetModule;
  index?: number;
  totalFavorites?: number;
  canAdd?: boolean;
  onMove?: (index: number, direction: 'left' | 'right') => void;
  onRemove?: (module: WidgetModule) => void;
  onAdd?: (module: WidgetModule) => void;
};

export const WidgetSlot = ({ module, index = 0, totalFavorites = 0, canAdd, onMove, onRemove, onAdd, ...props }: WidgetSlotProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  if (!module || !module.component) {
    if (!onMove && !onRemove) return null;
    return (
      <Box px={1} {...props}>
        <HStack
          w="full"
          h={215}
          flexShrink={0}
          space={2}
          justifyContent="center"
          alignItems="center"
          bgColor="gray.200"
          rounded="xl"
          p={2}
          borderColor="gray.400"
          borderWidth={3}
          borderStyle="dashed"
        >
          <PlusCircleIcon size={5} color={colors.gray[600]} />
          <Text size="lg" fontWeight={700} color="gray.600">
            {t('widgets:add_widget')}
          </Text>
        </HStack>
      </Box>
    );
  }

  const Widget = module.component as React.ComponentType<any>;
  const isFavorite = !!(onMove || onRemove);
  return (
    <Box px={1} {...props}>
      <VStack
        flex={1}
        space={1}
        bgColor="gray.200"
        rounded="xl"
        p={1.5}
        shadow={1}
        justifyContent={isFavorite ? undefined : 'space-between'}
      >
        <Box w="full" h={190}>
          <Widget flex={1} title={module?.title} titleTranslations={module?.titleTranslations} />
        </Box>

        {isFavorite && (
          <HStack flex={1} justifyContent="space-between">
            <HStack space={2}>
              {index !== 0 && onMove && (
                <IconButton
                  p={0}
                  size={8}
                  rounded="lg"
                  bgColor="white"
                  onPress={() => onMove(index, 'left')}
                  icon={<ArrowSquareLeftIcon size={4} color={colors.black} />}
                />
              )}
              {index !== totalFavorites - 1 && onMove && (
                <IconButton
                  p={0}
                  size={8}
                  rounded="lg"
                  bgColor="white"
                  onPress={() => onMove(index, 'right')}
                  icon={<ArrowSquareRightIcon size={4} color={colors.black} />}
                />
              )}
            </HStack>
            {onRemove && (
              <IconButton
                onPress={() => onRemove(module)}
                p={0}
                size={8}
                rounded="lg"
                bgColor="primary.500"
                icon={<MinusCircleIcon size={4} color={colors.black} />}
              />
            )}
          </HStack>
        )}

        {!isFavorite && canAdd && onAdd && (
          <IconButton
            onPress={() => onAdd(module)}
            p={0}
            size={8}
            rounded="lg"
            bgColor="primary.500"
            alignSelf="flex-end"
            icon={<PlusCircleIcon size={4} color={colors.black} />}
          />
        )}
      </VStack>
    </Box>
  );
};
